import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Loader2 } from "lucide-react";

export default function AuthModal() {
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (tab === "signin") await login(email, password);
      else await register(email, password, name);
      toast.success(tab === "signin" ? "Welcome back, degen." : "Account created. Let's ape.");
      setAuthOpen(false);
      setEmail(""); setPassword(""); setName("");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/scanner";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="glass border-[#334566] max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Zap className="h-5 w-5 text-emerald-400" />
            {tab === "signin" ? "Sign in to CatalystCaddy" : "Create your account"}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full bg-[#0d1420]">
            <TabsTrigger value="signin" data-testid="auth-tab-signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup" data-testid="auth-tab-signup">Sign Up</TabsTrigger>
          </TabsList>
        </Tabs>

        <form onSubmit={submit} className="space-y-3 mt-2">
          {tab === "signup" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Username <span className="text-slate-600">— what others see instead of your email</span></Label>
              <Input required value={name} onChange={(e) => setName(e.target.value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 20))} placeholder="degen_ape"
                     minLength={3} maxLength={20} pattern="[A-Za-z0-9_]{3,20}" title="3-20 characters: letters, numbers, underscore"
                     className="bg-[#0d1420] border-[#334566]" data-testid="auth-username-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@wallet.sol" className="bg-[#0d1420] border-[#334566]" data-testid="auth-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Password</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="••••••••" className="bg-[#0d1420] border-[#334566]" data-testid="auth-password-input" />
          </div>

          {err && <p className="text-sm text-red-400" data-testid="auth-error">{err}</p>}

          <Button type="submit" disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="auth-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tab === "signin" ? "Sign In" : "Create Account"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-1">
          <div className="h-px flex-1 bg-[#334566]" />
          <span className="text-xs text-slate-500 font-mono">OR</span>
          <div className="h-px flex-1 bg-[#334566]" />
        </div>

        <Button variant="outline" onClick={google}
                className="w-full border-[#334566] bg-transparent hover:bg-[#172030]" data-testid="auth-google-btn">
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>
        {window.navigator.standalone && (
          <p className="text-[11px] text-amber-300/90 text-center -mt-1" data-testid="auth-standalone-hint">
            Home-screen app: Google sign-in opens in Safari and won't carry over. Use email &amp; password here, or sign in from Safari.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
